import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  ChartTooltip,
  Flex,
  FlexColumn,
} from '@elementary/common-ui/components';
import ChartSelectionOverlay from '@elementary/common-ui/components/TestCharts/ChartControls/ChartSelectionOverlay';
import ChartZoomControls from '@elementary/common-ui/components/TestCharts/ChartControls/ChartZoomControls';
import { ZOOM_CONTROLS_MARGIN } from '@elementary/common-ui/components/TestCharts/constants';
import useChartSelection from '@elementary/common-ui/components/TestCharts/hooks/useChartSelection';
import themeBase from '@elementary/common-ui/style/theme';
import { numberWithCommas } from '@elementary/common-ui/utils/common';
import {
  formatShortDateTime,
  formatTimeValue,
  localizeDate,
} from '@elementary/common-ui/utils/datetime';
import { Stack, Typography, Divider, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import Humanize from 'humanize-plus';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  TooltipProps,
} from 'recharts';
import { MetricSource } from 'metricsExplorer/types';
import { radius, space } from '@elementary/common-ui/style/utils';

const EMPTY_STATE_MARGIN_LEFT = '75px';

const MILLISECONDS_IN_SECOND = 1000;
const DOTS_OVERLOAD_LIMIT = 200;
const X_TICKS_OVERLOAD_LIMIT = 20;
const TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

export type LinePalette = {
  primary: string;
  light: string;
};

export const LINES_PALETTE: Array<LinePalette> = [
  {
    primary: themeBase.palette.chart.series.plum,
    light: themeBase.palette.chart.seriesFill.plum,
  },
  {
    primary: themeBase.palette.chart.series.rose,
    light: themeBase.palette.chart.seriesFill.rose,
  },
  {
    primary: themeBase.palette.chart.series.red,
    light: themeBase.palette.chart.seriesFill.red,
  },
  {
    primary: themeBase.palette.chart.series.orange,
    light: themeBase.palette.chart.seriesFill.orange,
  },
  {
    primary: themeBase.palette.chart.series.teal,
    light: themeBase.palette.chart.seriesFill.teal,
  },
  {
    primary: themeBase.palette.chart.series.turquoise,
    light: themeBase.palette.chart.seriesFill.turquoise,
  },
  {
    primary: themeBase.palette.chart.series.aqua,
    light: themeBase.palette.chart.seriesFill.aqua,
  },
  {
    primary: themeBase.palette.chart.series.green,
    light: themeBase.palette.chart.seriesFill.green,
  },
  {
    primary: themeBase.palette.chart.series.lime,
    light: themeBase.palette.chart.seriesFill.lime,
  },
  {
    primary: themeBase.palette.chart.series.yellow,
    light: themeBase.palette.chart.seriesFill.yellow,
  },
  {
    primary: themeBase.palette.chart.series.blue,
    light: themeBase.palette.chart.seriesFill.blue,
  },
  {
    primary: themeBase.palette.chart.series.sky,
    light: themeBase.palette.chart.seriesFill.sky,
  },
  {
    primary: themeBase.palette.chart.series.navy,
    light: themeBase.palette.chart.seriesFill.navy,
  },
  {
    primary: themeBase.palette.chart.series.violet,
    light: themeBase.palette.chart.seriesFill.violet,
  },
  {
    primary: themeBase.palette.chart.series.lavender,
    light: themeBase.palette.chart.seriesFill.lavender,
  },
];

const METRIC_FORMATTER_MAP: Record<string, (value: number) => string> = {
  time_without_updates: formatTimeValue,
};

const ONLY_ENDDATE_METRICS = ['time_without_updates'];

export type DataPoint = {
  value?: number;
  startTime?: string;
  endTime?: string;
  dimensionValue?: string;
  metricName: string;
};

type ChartDataPoint = Omit<DataPoint, 'value'> & {
  value?: number | null;
  endTimeTimestamp: number;
  dotsCount?: number;
  index: number;
};

const EmptyStateWrapper = styled(Flex)`
  z-index: 1;
  position: absolute;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const ChartDot = styled.div<{
  $color: string;
  $highlightColor: string;
}>`
  width: 6px;
  height: 6px;
  background: ${({ $color }) => $color};
  border-radius: ${radius('round')};

  &:hover {
    border: 2px solid ${({ $highlightColor }) => $highlightColor};
    box-sizing: content-box;
    background-clip: padding-box;
    -webkit-background-clip: padding-box;
  }
`;

const NoTextSelectWrapper = styled(Stack)<{ $disableTextSelect?: boolean }>`
  border-radius: ${radius('lg')};
  background-color: ${({ theme }) => theme.palette.surface.subtle};
  position: relative;

  .recharts-cartesian-grid {
    cursor: crosshair;
  }

  ${({ $disableTextSelect }) =>
    $disableTextSelect &&
    `
      * {
        user-select: none !important;
      }
    `}
`;

const NoDataDescription = styled(Typography)`
  work-break: break-word;
  white-space: normal;
  color: ${({ theme }) => theme.palette.content.secondary};
  width: 80%;
  text-align: center;
`;

const RenderTooltip = (
  props: TooltipProps<any, any> & { color: string; hidden: boolean },
) => {
  const data = props?.payload?.[0]?.payload;

  if (data == null || props.hidden) return null;

  const startDate =
    data.startTime &&
    localizeDate(data.startTime).isValid() &&
    localizeDate(data.startTime).format('L LT');
  const endDate = localizeDate(data.endTime).format('L LT');
  const formatter =
    METRIC_FORMATTER_MAP[data.metricName] || ((v) => numberWithCommas(v, 2));
  const value = formatter(data.value);
  const shouldRenderStartDate =
    startDate &&
    startDate !== endDate &&
    !ONLY_ENDDATE_METRICS.includes(data.metricName);

  return (
    <ChartTooltip>
      <FlexColumn gap={1}>
        <Typography>
          {shouldRenderStartDate ? `${startDate} - ${endDate}` : endDate}
        </Typography>
        <Divider sx={{ borderColor: props.color, borderBottomWidth: '3px' }} />
        <Typography>
          {data.metricName}: {value}
        </Typography>
      </FlexColumn>
    </ChartTooltip>
  );
};

const Dot = ({
  cx,
  cy,
  color,
  highlightColor,
  active,
  payload,
  onDotHovered,
}: any) => {
  if ((payload?.dotsCount || 0) >= DOTS_OVERLOAD_LIMIT && !active) {
    // Skips points in case there are too much
    return null;
  }

  return (
    <foreignObject
      x={cx - 5}
      y={cy - 5}
      width="10px"
      height="10px"
      onMouseEnter={() => onDotHovered(true)}
      onMouseLeave={() => onDotHovered(false)}
    >
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <ChartDot $color={color} $highlightColor={highlightColor} />
      </Stack>
    </foreignObject>
  );
};

type Props = {
  dataPoints: DataPoint[];
  colorPalette?: LinePalette;
  loading?: boolean;
  source: MetricSource;
};

const MetricChart = ({
  dataPoints,
  colorPalette = LINES_PALETTE[0],
  loading = false,
  source,
}: Props) => {
  const [dotHovered, setDotHovered] = useState(false);
  const chartRef = useRef<HTMLDivElement | null>(null);

  const domainY = useMemo(() => {
    const valueArr: number[] = [];
    (dataPoints || []).forEach((i) => {
      if (i.value !== undefined) {
        valueArr.push(i.value);
      }
    });

    valueArr.sort((a, b) => a - b);
    const min = Math.floor(valueArr[0]);
    const max = Math.ceil(valueArr[valueArr.length - 1]);
    const topMargin = (max - min) * 0.05;
    return [min, max + topMargin];
  }, [dataPoints]);

  const data: ChartDataPoint[] = useMemo(() => {
    const result = (dataPoints || [])
      .map((i) => ({
        ...i,
        endTimeTimestamp: dayjs.utc(i.endTime, TIME_FORMAT).unix(),
      }))
      .sort((i, j) => i.endTimeTimestamp - j.endTimeTimestamp)
      .map((item, index) => ({
        ...item,
        index,
      }));

    return result.map((item) => ({
      ...item,
      dotsCount: result.length,
    }));
  }, [dataPoints]);

  const chart = useChartSelection({
    data,
    getXValue: (i: ChartDataPoint) => i.endTimeTimestamp,
    chartRef,
    mode: 'zoom',
  });

  const ticksX = useMemo(() => {
    if (chart.zoomedData.length <= X_TICKS_OVERLOAD_LIMIT) {
      // Use all ticks if there are not too much points
      return undefined;
    }
    const minTick = chart.zoomedData[0]?.endTimeTimestamp;
    const maxTick =
      chart.zoomedData[chart.zoomedData.length - 1]?.endTimeTimestamp;
    const ticksCount = 10;
    const step = Math.floor((maxTick - minTick) / ticksCount);
    const ticks: number[] = [];
    [...Array(ticksCount + 1).keys()].forEach((i) => {
      ticks.push(minTick + step * i);
    });
    return ticks;
  }, [chart.zoomedData]);

  return (
    <NoTextSelectWrapper
      ref={chartRef}
      $disableTextSelect={chart.selectionStartX !== null}
    >
      {loading && (
        <EmptyStateWrapper>
          <CircularProgress size={32} />
        </EmptyStateWrapper>
      )}
      {!loading && chart.zoomedData.length === 0 && (
        <EmptyStateWrapper>
          <NoDataDescription
            sx={{
              marginLeft: EMPTY_STATE_MARGIN_LEFT,
              marginRight: space('xl'),
            }}
          >
            {source === 'dbt' ? (
              <>
                No metrics were collected in the selected period of time.
                <br />
                Try selecting a different date range and make sure you ran
                &quot;dbt test&quot; after configuring the metric.
              </>
            ) : (
              <>
                This table was not found in query history in the selected
                period.
                <br />
                This might happen due to insufficient permissions or
                table&apos;s SQL query not running in the selected period.
              </>
            )}
          </NoDataDescription>
        </EmptyStateWrapper>
      )}

      <ChartZoomControls
        loading={loading}
        onZoomIn={chart.handleZoomIn}
        onZoomOut={chart.handleZoomOut}
        onReset={chart.handleResetZoom}
        disableZoomIn={chart.isMaxZoom}
        disableZoomOut={!chart.zoomRange}
        disableReset={!chart.zoomRange}
        sx={{ margin: ZOOM_CONTROLS_MARGIN }}
      />

      <ChartSelectionOverlay
        startX={chart.selectionStartX}
        currentX={chart.selectionCurrentX}
        top={48}
      />

      <ResponsiveContainer width="99%" height={300}>
        <ComposedChart
          data={chart.zoomedData}
          margin={{ top: 28, right: 4, bottom: 14, left: 14 }}
          onMouseDown={chart.handleMouseDown}
          onMouseMove={chart.handleMouseMove}
          onMouseUp={chart.handleMouseUp}
        >
          <CartesianGrid
            stroke={themeBase.palette.chart.grid}
            fill={themeBase.palette.icon.inverse}
          />

          <XAxis
            axisLine={false}
            tickLine={false}
            tickMargin={6}
            tickFormatter={(value) =>
              formatShortDateTime(localizeDate(value * MILLISECONDS_IN_SECOND))
            }
            dataKey="endTimeTimestamp"
            scale="time"
            type="number"
            domain={(range) => range}
            tick={{ fontSize: themeBase.tokens.fontSize.xs }}
            ticks={ticksX}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            tickMargin={6}
            dataKey="value"
            domain={domainY}
            tick={{ fontSize: themeBase.tokens.fontSize.md }}
            tickFormatter={(value) => Humanize.compactInteger(value)}
          />

          <Tooltip
            content={
              <RenderTooltip
                color={colorPalette.primary}
                hidden={chart.selectionStartX !== null || !dotHovered}
              />
            }
            isAnimationActive={false}
            cursor={false}
            allowEscapeViewBox={{ x: false, y: false }}
            wrapperStyle={{ pointerEvents: 'none', outline: 0 }}
          />

          {chart.zoomedData.length > 0 && (
            <Line
              data={chart.zoomedData}
              dataKey="value"
              stroke={colorPalette.primary}
              strokeWidth={2}
              dot={
                <Dot
                  color={colorPalette.primary}
                  highlightColor={colorPalette.light}
                  onDotHovered={setDotHovered}
                />
              }
              activeDot={
                <Dot
                  active
                  color={colorPalette.primary}
                  highlightColor={colorPalette.light}
                  onDotHovered={setDotHovered}
                />
              }
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </NoTextSelectWrapper>
  );
};

export default MetricChart;
